/* eslint-disable import/prefer-default-export */
import TimeInterval, { TimeIntervalStrings } from 'enums/TimeInterval'
import TimeUnitCategory, { getOptions as getTimeUnitCategoryOptions } from 'enums/TimeUnitCategory'
import { SocialWebSourcesWidget } from 'schemas/SocialWebSourcesWidgetSchema'
import { SocialWebTimelineWidget } from 'schemas/SocialWebTimelineWidgetSchema'
import { AudienceTimelineWidget } from 'schemas/AudienceTimelineWidgetSchema'
import { YoutubeTimelineWidget } from 'schemas/YoutubeTimelineWidgetSchema'
import { getSearchTopics, WidgetInfo } from './widgetUtil'
import { date } from './dateUtil'
import DateRange from '../vo/DateRange'

export const getTINumber = (timeInterval: TimeIntervalStrings) => {
  switch (timeInterval) {
    case TimeInterval.LAST_HOUR:
      return date.MS_IN_HOUR
    case TimeInterval.LAST_DAY:
      return date.MS_IN_DAY
    case TimeInterval.LAST_WEEK:
      return date.MS_IN_WEEK
    case TimeInterval.LAST_MONTH:
      return date.MS_IN_MONTH
    case TimeInterval.LAST_QUARTER:
      return date.MS_IN_MONTH * 3
    case TimeInterval.LAST_SIX_MONTHS:
      return date.MS_IN_MONTH * 6
    case TimeInterval.LAST_YEAR:
      return date.MS_IN_YEAR
    case TimeInterval.LAST_15MONTHS:
      return date.MS_IN_MONTH * 15
    case TimeInterval.LAST_18MONTHS:
      return date.MS_IN_MONTH * 18
    case TimeInterval.LAST_21MONTHS:
      return date.MS_IN_MONTH * 21
    case TimeInterval.LAST_2YEARS:
      return date.MS_IN_YEAR * 2
    case TimeInterval.LAST_27MONTHS:
      return date.MS_IN_MONTH * 27
    case TimeInterval.ALL:
    case TimeInterval.OPEN_END:
    case TimeInterval.CUSTOM:
    default:
      return -1
  }
}

export const getDateRangeByType = (
  interval: TimeIntervalStrings,
  now?: Date,
  from?: Date,
  to?: Date
) => {
  const dateNow = now || new Date()
  let start = new Date(dateNow)
  let end = new Date(dateNow)
  const tiNum = getTINumber(interval)
  if (tiNum !== -1) {
    start.setTime(start.getTime() - tiNum)
  } else {
    switch (interval) {
      case TimeInterval.OPEN_END:
        start = from || start
        break
      case TimeInterval.CUSTOM:
        start = from || start
        end = to || end
        break
      case TimeInterval.ALL:
      default:
        start.setTime(0)
        break
    }
  }
  const dateRange = DateRange()
  dateRange.start = start.getTime()
  dateRange.end = end.getTime()
  return dateRange
}

const getTUCategoryByDiff = (diff: number) => {
  if (diff > date.MS_IN_YEAR) {
    return TimeUnitCategory.OVER_1Y
  }
  if (diff > date.MS_IN_MONTH * 3) {
    return TimeUnitCategory.OVER_3M
  }
  if (diff > date.MS_IN_WEEK) {
    return TimeUnitCategory.OVER_1W
  }
  if (diff > date.MS_IN_DAY) {
    return TimeUnitCategory.OVER_1D
  }
  if (diff > date.MS_IN_HOUR) {
    return TimeUnitCategory.OVER_1H
  }
  if (diff >= 0) {
    return TimeUnitCategory.UNDER_1H
  }
  throw new Error(`cannot get time unit by time difference: ${diff}`)
}

type TimeUnitWidgets =
  | SocialWebSourcesWidget
  | SocialWebTimelineWidget
  | AudienceTimelineWidget
  | YoutubeTimelineWidget

export const getTimeUnitCategory = (widget: TimeUnitWidgets) => {
  const now = new Date()
  let topicsStartTime = now.getTime()
  let topicsEndTime = 0
  const topics = getSearchTopics(widget as WidgetInfo)
  for (let i = 0; i < topics.length; i += 1) {
    const dateRange = getDateRangeByType(
      topics[i].timeInterval!,
      now,
      new Date(topics[i].fromDate!), // TODO: should instead convert fromDate/toDate to number from schema
      new Date(topics[i].toDate!)
    )
    if (topicsStartTime > dateRange.start!) {
      topicsStartTime = dateRange.start!
    }
    if (topicsEndTime < dateRange.end!) {
      topicsEndTime = dateRange.end!
    }
  }

  const filterDateRange = getDateRangeByType(
    widget.timeInterval!,
    now,
    new Date(widget.startDatetime!), // TODO: nb.dto.toDateTime
    new Date(widget.endDatetime!) // TODO: nb.dto.toDateTime
  )
  const filterStartTime = filterDateRange.start!
  const filterEndTime = filterDateRange.end!

  let diff
  if (filterStartTime > topicsEndTime || filterEndTime < topicsStartTime) {
    diff = filterEndTime - filterStartTime
  } else {
    const start = filterStartTime > topicsStartTime ? filterStartTime : topicsStartTime
    const end = filterEndTime < topicsEndTime ? filterEndTime : topicsEndTime
    diff = end - start
  }
  return getTUCategoryByDiff(diff)
}

export const getOptions = (widget: TimeUnitWidgets) => {
  const timeCategory = getTimeUnitCategory(widget)

  return getTimeUnitCategoryOptions(timeCategory)
}
